import { AddBoxOutlined } from '@mui/icons-material';
import { Box, Button } from '@mui/material';
import { BodyText, CustomChip, LoadingPaperPlane, SubTitleText } from 'Application/components';
import { ExchangeRatesTable } from 'Application/components/ExchangeRatesTable';
import { ExchangeRateInfo } from 'Application/models';
import { useExchangeRateStore } from 'Application/store';
import { changeDateFormat } from 'Application/utils';
import { FC, useEffect, useState } from 'react';

interface ExchangeRatesProps {
    addExchangeRateDialog: () => void;
    isExchangeRateAdded: boolean;
    setIsAddedRate: React.Dispatch<React.SetStateAction<boolean>>;
}

export const ExchangeRates: FC<ExchangeRatesProps> = ({ addExchangeRateDialog, isExchangeRateAdded, setIsAddedRate }) => {
    const {
        isLoading: isGetExchangeRatesLoading,
        allExchangeRates,
        getAllExchangeRates
    } = useExchangeRateStore((state) => ({
        isLoading: state.isLoading,
        allExchangeRates: state.allExchangeRates,
        getAllExchangeRates: state.getAllExchangeRates
    }));
    const [data, setData] = useState<ExchangeRateInfo[]>([]);
    const exchangeRatesCount = data.length;

    // Used for counting the times the component is rendered (for controlling the data fetching calls).
    const [countRender, setCountRender] = useState<number>(0);

    const [isExchangeRatesChange, setIsExchangeRatesChange] = useState<boolean>(false);

    useEffect(() => {
        if (countRender > 0) {
            return;
        }

        getAllExchangeRates();

        /* Check whether allExchangeRates is not null and countRender is equal to zero
        (to ensure that the data is present and the API call for getting all exchange rates are not
        called infinite times). */
        if (allExchangeRates && countRender === 0) {
            const allExchangeRatesInfo: ExchangeRateInfo[] = allExchangeRates.map((exchangeRate) => {
                const newUpdatedAt = changeDateFormat(exchangeRate?.updatedAt);
                const newUpdatedBy = exchangeRate?.updatedBy === null ? '---' : exchangeRate?.updatedBy;

                const exchangeRateInfo: ExchangeRateInfo = {
                    fromCurrency: exchangeRate?.fromCurrency,
                    toCurrency: exchangeRate?.toCurrency,
                    rate: exchangeRate?.rate,
                    updatedBy: newUpdatedBy,
                    updatedAt: newUpdatedAt
                };

                return exchangeRateInfo;
            });

            setData(allExchangeRatesInfo);
            setCountRender((prevCountRender) => prevCountRender + 1);
        }
    }, [allExchangeRates, countRender]);

    useEffect(() => {
        if (isExchangeRatesChange) {
            setCountRender((prevCountRender) => prevCountRender - 1);
            setIsExchangeRatesChange(false);
        }
    }, [isExchangeRatesChange]);

    useEffect(() => {
        if (isExchangeRateAdded) {
            setIsExchangeRatesChange(true);
            setIsAddedRate(false);
        }
    }, [isExchangeRateAdded]);

    return (
        <Box className="border rounded-xl shadow-sm border-slate-200">
            <Box className="flex flex-wrap justify-between items-center p-6" id="header-section">
                <Box className="flex flex-col space-y-2" id="title-section">
                    <Box className="flex items-end space-x-2">
                        <SubTitleText text={'Exchange Rates'} />
                        <CustomChip text={`${exchangeRatesCount} exchange rates`} />
                    </Box>
                    <BodyText text={'Manage the exchange rates covered by this product'} />
                </Box>
                <Button
                    onClick={addExchangeRateDialog}
                    className="text-sky-700 border-sky-200 bg-sky-50 rounded-md mt-2 md:mt-0"
                    variant="outlined"
                    startIcon={<AddBoxOutlined />}
                >
                    Add Exchange Rate
                </Button>
            </Box>
            {isGetExchangeRatesLoading ? (
                <LoadingPaperPlane />
            ) : (
                <ExchangeRatesTable data={data} setIsExchangeRatesChange={setIsExchangeRatesChange} hasCheckboxSelection />
            )}
        </Box>
    );
};
